import Breadcrumbs from '@/components/Breadcrumbs';
import PrimaryButton from '@/components/Buttons/PrimaryButton';
import SecondaryButton from '@/components/Buttons/SecondaryButton';
import ThemeWrapper from '@/components/ThemeWrapper';
import useThemeColors from '@/hooks/useThemeColors';
import AppLayout from '@/layouts/AppLayout';
import { Evaluation } from '@/types';
import { router } from '@inertiajs/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { cleanWordPressHtml } from '@/utils/cleanHtml';

interface InstructionsProps {
    evaluation: Evaluation;
    course: {
        slug: string;
    };
}

const InstructionsContent: React.FC<InstructionsProps> = ({ evaluation, course }) => {
    const { t } = useTranslation();
    const colors = useThemeColors();

    const breadcrumbItems = [
        {
            label: t('course.sectionDetail.courses'),
            href: `/courses/${course.slug}/`,
        },
        { label: evaluation.title },
    ];

    const questionCount = evaluation.questions_set === 'subset' 
        ? evaluation.questions_subset_qty 
        : evaluation.questions?.length || 0;

    const formatTime = (minutes: number | null) => {
        if (!minutes) return null;
        
        if (minutes < 60) {
            return t('evaluation.instructions.minutesFormat', { count: minutes });
        }
        
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        
        if (remainingMinutes === 0) {
            return t('evaluation.instructions.hoursFormat', { count: hours });
        }
        
        return t('evaluation.instructions.hoursMinutesFormat', { 
            hours, 
            minutes: remainingMinutes 
        });
    };

    const handleStart = () => {
        router.post(`/evaluations/${evaluation.id}/start/`);
    };

    const handleBack = () => {
        router.get(`/courses/${course.slug}/`);
    };

    return (
        <AppLayout>
            <div className="min-h-screen bg-gray-50">
                <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6 lg:px-8">
                    {/* Breadcrumbs */}
                    <div className="mb-6">
                        <Breadcrumbs items={breadcrumbItems} />
                    </div>

                    {/* Main Content Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        {/* Hero Section */}
                        <div className={`relative bg-gradient-to-br px-8 py-12 ${colors.headerBg}`}>
                            <div className={`absolute inset-0 bg-gradient-to-r ${colors.gradientFrom}/5 ${colors.gradientTo}/5`}></div>
                            <div className="relative">
                                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6 ${colors.primaryBg}`}>
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h1 className="text-3xl font-bold text-gray-900 mb-3">
                                    {evaluation.title}
                                </h1>
                                {evaluation.description && (
                                    <div
                                        className="text-lg text-gray-600 leading-relaxed max-w-2xl"
                                        dangerouslySetInnerHTML={{ __html: cleanWordPressHtml(evaluation.description) }}
                                    />
                                )}
                            </div>
                        </div>

                        {/* Content Section */}
                        <div className="px-8 py-8">
                            {/* Stats Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                                <div className="text-center p-6 rounded-xl bg-gray-50 border border-gray-100">
                                    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg mb-4 ${colors.iconBg}`}>
                                        <svg className={`w-6 h-6 ${colors.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <div className="text-2xl font-bold text-gray-900 mb-1">{questionCount}</div>
                                    <div className="text-sm font-medium text-gray-600">
                                        {t('evaluation.instructions.questionCount', { count: questionCount })}
                                    </div>
                                </div>

                                {evaluation.time_limit_minutes && (
                                    <div className="text-center p-6 rounded-xl bg-gray-50 border border-gray-100">
                                        <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg mb-4 ${colors.iconBg}`}>
                                            <svg className={`w-6 h-6 ${colors.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <div className="text-2xl font-bold text-gray-900 mb-1">{evaluation.time_limit_minutes}</div>
                                        <div className="text-sm font-medium text-gray-600">
                                            {t('evaluation.instructions.timeLimit', { 
                                                time: formatTime(evaluation.time_limit_minutes) 
                                            })}
                                        </div>
                                    </div>
                                )}

                                <div className="text-center p-6 rounded-xl bg-gray-50 border border-gray-100">
                                    <div className="inline-flex items-center justify-center w-12 h-12 bg-emerald-100 rounded-lg mb-4">
                                        <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                        </svg>
                                    </div>
                                    <div className="text-2xl font-bold text-gray-900 mb-1">{evaluation.passing_score}%</div>
                                    <div className="text-sm font-medium text-gray-600">
                                        {t('evaluation.instructions.passingScore', { 
                                            score: evaluation.passing_score 
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* Ready Section */}
                            <div className="text-center mb-8">
                                <h2 className="text-xl font-semibold text-gray-900 mb-3">
                                    ¿Estás listo para comenzar?
                                </h2>
                                <p className="text-gray-600 max-w-md mx-auto">
                                    Una vez que inicies la evaluación, el tiempo comenzará a correr.
                                </p>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                                <SecondaryButton onClick={handleBack} size="md" className="min-w-32">
                                    {t('evaluation.instructions.back')}
                                </SecondaryButton>
                                
                                <PrimaryButton onClick={handleStart} size="md" className="min-w-32">
                                    {t('evaluation.instructions.start')}
                                </PrimaryButton>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
};

const Instructions: React.FC<InstructionsProps> = (props) => {
    return (
        <ThemeWrapper>
            <InstructionsContent {...props} />
        </ThemeWrapper>
    );
};

export default Instructions;